import React, { useEffect, useState } from "react";
import "../styles/PageSelection.scss";
import MCQSummary from "./MCQSummary";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignLeft,
  faFile,
  faPenToSquare,
  faBookOpen,
  faChevronUp,
  faChevronDown,
  faFolder,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import KeyboardDoubleArrowLeft from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import TooltipComponent from "./tooltipComponent";
import lockIcon from "../assets/png/lockIcon.png";
import Cookies from "js-cookie";
import { checkIsAdmin } from "../helper/authUtils";

const PageSelection = ({
  subtopic,
  title = "Items",
  items = [],
  activeId,
  onSelect = () => {},
  leftIcon,
  emptyText = "No items to show",
  mcqProps,
  completed,
  isAdmin: isAdminProp,
  isPageLocked = false,
  level = "page",
  parentKey = "",
  onNextOverride,
  onPrevOverride,
  disableNextOverride,
  disablePrevOverride,
  showNavigation = false,
}) => {
  const isAdmin = isAdminProp !== undefined ? isAdminProp : checkIsAdmin();
  const { questions = [], answers = {} } = mcqProps || {};
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [isMcqCollapsed, setIsMcqCollapsed] = useState(false);
  const [internalActiveId, setInternalActiveId] = useState(activeId);
  const [expandedItems, setExpandedItems] = useState({});
  const answeredCount = Object.keys(answers).length || 0;
  const totalCount = questions.length;

  const flattenedStatuses = React.useMemo(() => {
    const res = {};
    const traverse = (arr) => {
      if (!arr || !Array.isArray(arr)) return;
      arr.forEach((it) => {
        const pageId =
          typeof it.page === "object"
            ? it.page?._id || it.page?.id
            : it.page || it.active_recall || it.active_recall_id;
        if (pageId) {
          res[pageId] = {
            completed: it.completed,
            content_quiz_completed: it.content_quiz_completed,
            teacher_completed: it.teacher_completed,
            is_locked: it.is_locked,
          };
        }
        if (it.child_pages) traverse(it.child_pages);
      });
    };
    
    const sourceCompleted = completed;
    const dataPart = sourceCompleted?.data;
    if (Array.isArray(dataPart)) {
      traverse(dataPart);
    } else if (dataPart && Array.isArray(dataPart.data)) {
      traverse(dataPart.data);
    }
    
    const activeRecallPart = sourceCompleted?.active_recall;
    if (Array.isArray(activeRecallPart)) {
      traverse(activeRecallPart);
    } else if (dataPart && Array.isArray(dataPart.active_recall)) {
      traverse(dataPart.active_recall);
    }
    
    return res;
  }, [completed]);

  const isPageUnlocked = (item) => {
    if (isAdmin) return true;
    if (item.is_institution) return true;
    if (
      item.page_type === "active_recall" ||
      item.page_type === "active_recall_test" ||
      item.page_type === "active_recall_content" ||
      item.page_type === "materials" ||
      item.page_type === "material_item"
    )
      return true;

    const itemIndex = navigableItems.findIndex(
      (it) => String(it.id) === String(item.id),
    );
    if (itemIndex > 0) {
      const prev = navigableItems[itemIndex - 1];
      if (
        prev &&
        (prev.page_type === "test" || prev.page_type === "active_recall_test")
      ) {
        const prevStatus = flattenedStatuses[prev.id];
        if (
          !prevStatus ||
          !prevStatus.completed ||
          (prev.page_type !== "active_recall_test" && !prevStatus.content_quiz_completed)
        ) {
          return false;
        }
      }
    }

    const status = flattenedStatuses[item.id];
    if (status && status.is_locked !== undefined) {
      if (status.completed) return true;
      return !status.is_locked && status.teacher_completed !== false;
    }

    // Fallback if status not found, assume it might be the first page or data not fully loaded
    const firstItem = items?.find((it) => !it.header);
    if (firstItem && item.id === firstItem.id) return true;

    return false;
  };

  const isItemLocked = (item) => {
    return !isAdmin && !item.is_institution && !isPageUnlocked(item);
  };

  const navigableItems = React.useMemo(() => {
    if (!items) return [];
    return items.filter((item) => {
      const type = item.page_type?.toLowerCase();
      return !(
        type === "subject" ||
        type === "topics" ||
        type === "subtopics" ||
        type === "courses"
      );
    });
  }, [items]);

  const currentActiveId = activeId !== undefined ? activeId : internalActiveId;

  const activeIndex = React.useMemo(() => {
    const isOrHasId = (item, targetId) => {
      if (String(item.id) === String(targetId)) return true;
      if (item.child_pages && item.child_pages.length > 0) {
        return item.child_pages.some((child) => isOrHasId(child, targetId));
      }
      return false;
    };

    return navigableItems.findIndex((item) => isOrHasId(item, currentActiveId));
  }, [navigableItems, currentActiveId]);

  const nextItem = React.useMemo(() => {
    if (activeIndex === -1 && navigableItems.length > 0) {
      return navigableItems[0];
    }
    if (activeIndex >= 0 && activeIndex < navigableItems.length - 1) {
      return navigableItems[activeIndex + 1];
    }
    return null;
  }, [navigableItems, activeIndex]);

  const prevItem = React.useMemo(() => {
    if (activeIndex > 0) {
      return navigableItems[activeIndex - 1];
    }
    return null;
  }, [navigableItems, activeIndex]);

  const isNextDisabled = onNextOverride
    ? disableNextOverride
    : !nextItem || isItemLocked(nextItem);
  const isPrevDisabled = onPrevOverride
    ? disablePrevOverride
    : !prevItem || isItemLocked(prevItem);

  const handleNext = () => {
    if (onNextOverride) {
      onNextOverride();
      return;
    }
    if (nextItem && !isItemLocked(nextItem)) {
      setInternalActiveId(nextItem.id);
      Cookies.set(getCookieName(), nextItem.id, { expires: 7 });
      onSelect(nextItem);
      setIsMcqCollapsed(false);
    }
  };

  const handlePrev = () => {
    if (onPrevOverride) {
      onPrevOverride();
      return;
    }
    if (prevItem && !isItemLocked(prevItem)) {
      setInternalActiveId(prevItem.id);
      Cookies.set(getCookieName(), prevItem.id, { expires: 7 });
      onSelect(prevItem);
      setIsMcqCollapsed(false);
    }
  };

  const getCookieName = () => {
    if (parentKey) {
      return `current_page_id_${level}_${parentKey}`;
    }
    return `current_page_id_${level}`;
  };

  const setPresentPageId = (pageId) => {
    Cookies.set("present_pageid", pageId, { expires: 7 });
  };

  useEffect(() => {
    if (activeId !== undefined && activeId !== null && activeId !== "") {
      setInternalActiveId(activeId);
      setPresentPageId(activeId);
    }
  }, [activeId]);

  useEffect(() => {
    if (
      (activeId === undefined || activeId === null || activeId === "") &&
      (!internalActiveId ||
        internalActiveId === null ||
        internalActiveId === "") &&
      items &&
      items.length > 0
    ) {
      const cookieName = getCookieName();
      const savedId = Cookies.get(cookieName);
      const presentPageId = Cookies.get("present_pageid");
      let foundSaved = null;
      let foundPresentPage = null;

      const findItem = (list, id) => {
        for (const item of list) {
          if (String(item.id) === String(id)) return item;
          if (item.child_pages && item.child_pages.length > 0) {
            const found = findItem(item.child_pages, id);
            if (found) return found;
          }
        }
        return null;
      };

      const isSubtopic = subtopic === true;

      if (presentPageId && (!isSubtopic || presentPageId !== "subtopics")) {
        foundPresentPage = findItem(items, presentPageId);
      }

      if (foundPresentPage) {
        setInternalActiveId(foundPresentPage.id);
        setPresentPageId(foundPresentPage.id);
        onSelect(foundPresentPage);
        Cookies.set(cookieName, foundPresentPage.id, { expires: 7 });
      } else if (savedId && (!isSubtopic || savedId !== "subtopics")) {
        foundSaved = findItem(items, savedId);
        if (foundSaved) {
          setInternalActiveId(foundSaved.id);
          setPresentPageId(foundSaved.id);
          onSelect(foundSaved);
        } else {
          if (savedId) Cookies.remove(cookieName);
          if (presentPageId) Cookies.remove("present_pageid");
          const first = items.find(
            (it) => !it.header && (!isSubtopic || it.id !== "subtopics"),
          );
          if (first) {
            setInternalActiveId(first.id);
            setPresentPageId(first.id);
            Cookies.set(cookieName, first.id, { expires: 7 });
          }
        }
      } else {
        const first = items.find(
          (it) => !it.header && (!isSubtopic || it.id !== "subtopics"),
        );
        if (first) {
          setInternalActiveId(first.id);
          setPresentPageId(first.id);
          Cookies.set(cookieName, first.id, { expires: 7 });
        }
      }
    }
  }, [items, activeId, internalActiveId, subtopic]);

  useEffect(() => {
    if (activeId || internalActiveId) {
      const currentId = activeId !== undefined ? activeId : internalActiveId;
      const parent = items.find((item) =>
        item.child_pages?.some(
          (child) => String(child.id) === String(currentId),
        ),
      );
      if (parent) {
        setExpandedItems((prev) => {
          if (prev[parent.id]) return prev;
          return {
            ...prev,
            [parent.id]: true,
          };
        });
      }
    }
  }, [activeId, internalActiveId, items]);

  const getItemIcon = (item, index, isChild = false) => {
    if (isChild) {
      if (
        item.page_type === "content" ||
        item.page_type === "active_recall_content"
      ) {
        return faFile;
      } else if (item.page_type === "quiz" || item.page_type === "mcq") {
        return faBookOpen;
      } else if (
        item.page_type === "test" ||
        item.page_type === "active_recall_test"
      ) {
        return faPenToSquare;
      } else if (item.page_type === "match") {
        return faBookOpen;
      } else if (
        item.page_type === "materials" ||
        item.page_type === "material_item"
      ) {
        return faFolder;
      }
      return faFile;
    }

    if (index === 0 && !subtopic) {
      return faAlignLeft;
    } else if (
      item.page_type === "content" ||
      item.page_type === "active_recall_content"
    ) {
      return faFile;
    } else if (item.page_type === "quiz" || item.page_type === "mcq") {
      return faBookOpen;
    } else if (
      item.page_type === "test" ||
      item.page_type === "active_recall_test"
    ) {
      return faPenToSquare;
    } else if (
      item.page_type === "materials" ||
      item.page_type === "material_item"
    ) {
      return faFolder;
    }
    return faFile;
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div
      className={`ps-panel ${isPanelCollapsed ? "ps-panel--collapsed" : ""}`}
    >
      {isPanelCollapsed ? (
        <>
          <div className="ps-icon-list custom-scrollbar">
            {items &&
              items.length > 0 &&
              items.map((item, index) => {
                const currentActive =
                  activeId !== undefined ? activeId : internalActiveId;
                const isActive = String(currentActive) === String(item.id);
                const isLocked =
                  !isAdmin && !item.is_institution && !isPageUnlocked(item);
                const isSubtopic = subtopic === true;

                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`ps-icon-item ${
                      isSubtopic && index === 0
                        ? "ps-icon-item--unclickable"
                        : isActive
                          ? "ps-icon-item--active"
                          : ""
                    }`}
                    onClick={() => {
                      if (isSubtopic && index === 0) return;
                      if (isLocked) return;
                      if (isActive) return;
                      setInternalActiveId(item.id);
                      Cookies.set(getCookieName(), item.id, { expires: 7 });
                      onSelect(item);
                      setIsMcqCollapsed(false);
                    }}
                    title={item.name}
                    style={{
                      cursor: isSubtopic && index === 0 ? "default" : (isLocked ? "not-allowed" : "pointer"),
                      position: "relative",
                    }}
                  >
                    {isLocked && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "rgba(255, 255, 255, 0.7)",
                          zIndex: 10,
                          borderRadius: "inherit",
                          backdropFilter: "blur(2px)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={lockIcon}
                          height={16}
                          width={16}
                          alt="Locked"
                        />
                      </div>
                    )}
                    <FontAwesomeIcon
                      icon={getItemIcon(item, index)}
                      className="ps-icon-item__icon"
                    />
                  </button>
                );
              })}
          </div>
          <button
            type="button"
            className="ps-show-all-btn"
            onClick={() => setIsPanelCollapsed(false)}
            title="Show All"
          >
            <KeyboardDoubleArrowLeft fontSize="small" />
          </button>
        </>
      ) : (
        <>
          <div className="ps-panel__list custom-scrollbar">
            {items && items.length > 0 ? (
              items.map((item, index) => {
                const currentActive =
                  activeId !== undefined ? activeId : internalActiveId;
                const isActive = String(currentActive) === String(item.id);
                const type = item.page_type?.toLowerCase();
                const completematch = flattenedStatuses[item.id];
                const isLocked =
                  !isAdmin && !item.is_institution && !isPageUnlocked(item);
                const isQuizType = [
                  "quiz",
                  "mcq",
                  "truefalse",
                  "trueorfalse",
                  "fillups",
                  "match",
                ].includes(type);
                const hasMcq = isActive && mcqProps && isQuizType && totalCount > 1;
                const hasChildren =
                  item.child_pages && item.child_pages.length > 0;
                const isSubtopic = subtopic === true;

                return (
                  <div key={item.id} className="ps-row-wrapper">
                    <div
                      key={item.id}
                      className={`ps-row ${
                        isSubtopic && index === 0
                          ? "ps-row--unclickable"
                          : isActive &&
                            (item.page_type === "mcq" || item.page_type === "quiz")
                            ? "ps-row-collapse-active"
                            : isActive
                              ? "ps-row--active"
                              : ""
                      }`}
                      onClick={() => {
                        if (isSubtopic && index === 0) return;
                        if (isLocked) return;
                        if (isActive) return;
                        if (hasChildren) {
                          setExpandedItems((prev) => ({
                            ...prev,
                            [item.id]: true,
                          }));
                        } else {
                          setInternalActiveId(item.id);
                          Cookies.set(getCookieName(), item.id, {
                            expires: 7,
                          });
                        }
                        onSelect(item);
                        setIsMcqCollapsed(false);
                      }}
                      style={{
                        cursor: isSubtopic && index === 0 ? "default" : (isLocked ? "not-allowed" : "pointer"),
                        position: "relative",
                      }}
                    >
                      {isLocked && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                            zIndex: 10,
                            borderRadius: "inherit",
                            backdropFilter: "blur(2px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={lockIcon}
                            height={24}
                            width={24}
                            alt="Locked"
                          />
                        </div>
                      )}
                      <div className="ps-row__left">
                        <div
                          style={{
                            width:
                              item.page_type === "active_recall_test" ||
                              item.page_type === "active_recall_content" ||
                              (index === 0 && showNavigation)
                                ? ""
                                : "100%",
                          }}
                          className="ps-row__icon-wrapper"
                        >
                          <FontAwesomeIcon
                            icon={getItemIcon(item, index)}
                            className="ps-row__icon"
                          />
                          <TooltipComponent
                            className="ps-row__label"
                            maxWidth={200}
                          >
                            {item.name}
                          </TooltipComponent>
                          {item.is_institution && (
                            <span className="ps-row__chip">
                              Institute Resources
                            </span>
                          )}
                        </div>
                        {index === 0 && showNavigation && (
                          <div
                            className="ps-row__nav"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginLeft: "auto",
                            }}
                          >
                            <button
                              type="button"
                              className="ps-nav-btn"
                              onClick={handlePrev}
                              disabled={isPrevDisabled}
                              title="Previous"
                              style={{
                                width: "24px",
                                height: "24px",
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faChevronLeft}
                                fontSize="10px"
                              />
                            </button>
                            <button
                              type="button"
                              className="ps-nav-btn"
                              onClick={handleNext}
                              disabled={isNextDisabled}
                              title="Next"
                              style={{
                                width: "24px",
                                height: "24px",
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faChevronRight}
                                fontSize="10px"
                              />
                            </button>
                          </div>
                        )}
                        <div>
                          {completematch &&
                            !item.is_institution &&
                            !(
                              item.page_type === "materials" ||
                              item.page_type === "material_item"
                            ) && (
                              <span className="ps-row__completed">
                                {(
                                  item.page_type === "test"
                                    ? isAdmin
                                      ? completematch?.completed
                                      : completematch?.completed &&
                                        completematch?.content_quiz_completed
                                    : completematch?.completed
                                )
                                  ? "Completed"
                                  : "Incomplete"}
                              </span>
                            )}
                        </div>
                        {hasChildren && (
                          <div
                            className="ps-row__dropdown-toggle"
                            onClick={(e) => {
                              if (isLocked) return;
                              e.stopPropagation();
                              setExpandedItems((prev) => ({
                                ...prev,
                                [item.id]: !prev[item.id],
                              }));
                            }}
                            style={{
                              cursor: isLocked ? "not-allowed" : "pointer",
                              display: "flex",
                              marginLeft: "auto",
                              padding: "0 8px",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={
                                expandedItems[item.id]
                                  ? faChevronUp
                                  : faChevronDown
                              }
                              className="ps-row__dropdown-icon"
                            />
                          </div>
                        )}
                        {hasMcq && (
                          <div className="mcq-index-right">
                            <span className="mcq-index-count">
                              {answeredCount}/{totalCount}
                            </span>
                            <div
                              onClick={(e) => {
                                if (isLocked) return;
                                e.stopPropagation();
                                setIsMcqCollapsed(!isMcqCollapsed);
                              }}
                              style={{
                                cursor: isLocked ? "not-allowed" : "pointer",
                                display: "flex",
                              }}
                            >
                              <FontAwesomeIcon
                                icon={
                                  isMcqCollapsed ? faChevronDown : faChevronUp
                                }
                                className="mcq-index-arrow"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      {hasMcq && !isMcqCollapsed && (
                        <div
                          onClick={(e) => {
                            if (isLocked) return;
                            e.stopPropagation();
                          }}
                          style={{
                            cursor: isLocked ? "not-allowed" : "default",
                            width: "100%",
                          }}
                        >
                          <MCQSummary {...mcqProps} contentTitle={item.name} />
                        </div>
                      )}
                    </div>

                    {hasChildren && expandedItems[item.id] && (
                      <div className="ps-child-pages">
                        {item.child_pages
                          ?.slice()
                          .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                          .map((childItem, childIndex) => {
                            const isChildActive =
                              String(currentActive) === String(childItem.id);
                            const childType =
                              childItem.page_type?.toLowerCase();
                            const isChildQuizType = [
                              "quiz",
                              "mcq",
                              "truefalse",
                              "trueorfalse",
                              "fillups",
                              "match",
                            ].includes(childType);
                            const isChildLocked =
                              !isAdmin &&
                              !childItem.is_institution &&
                              !isPageUnlocked(childItem);
                            const hasChildMcq =
                              isChildActive && mcqProps && isChildQuizType && totalCount > 1;

                            return (
                              <div
                                key={childItem.id}
                                className="ps-row-wrapper"
                              >
                                <div
                                  className={`ps-row ps-row--child ${
                                    isChildActive && isChildQuizType
                                      ? "ps-row-collapse-active"
                                      : isChildActive
                                        ? "ps-row--active"
                                        : ""
                                  }`}
                                  onClick={() => {
                                    if (isChildLocked) return;
                                    if (isChildActive) return;
                                    setInternalActiveId(childItem.id);
                                    Cookies.set(getCookieName(), childItem.id, {
                                      expires: 7,
                                    });
                                    onSelect(childItem);
                                    setIsMcqCollapsed(false);
                                  }}
                                  style={{
                                    paddingLeft: "1rem",
                                    cursor: isChildLocked
                                      ? "not-allowed"
                                      : "pointer",
                                    position: "relative",
                                  }}
                                >
                                  {isChildLocked && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor:
                                          "rgba(255, 255, 255, 0.7)",
                                        zIndex: 10,
                                        borderRadius: "inherit",
                                        backdropFilter: "blur(2px)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <img
                                        src={lockIcon}
                                        height={24}
                                        width={24}
                                        alt="Locked"
                                      />
                                    </div>
                                  )}
                                  <div className="ps-row__left">
                                    <div
                                      style={{
                                        width:
                                          item.page_type ===
                                            "active_recall_test" ||
                                          item.page_type ===
                                            "active_recall_content"
                                            ? ""
                                            : "100%",
                                      }}
                                      className="ps-row__icon-wrapper"
                                    >
                                      <FontAwesomeIcon
                                        icon={getItemIcon(
                                          childItem,
                                          childIndex,
                                          true,
                                        )}
                                        className="ps-row__icon"
                                      />
                                      <TooltipComponent
                                        className="ps-row__label"
                                        maxWidth={200}
                                      >
                                        {childItem.name}
                                      </TooltipComponent>
                                      {childItem.is_institution && (
                                        <span className="ps-row__chip">
                                          Additional Notes
                                        </span>
                                      )}
                                    </div>
                                    <div>
                                      {flattenedStatuses[childItem.id] &&
                                        !childItem.is_institution &&
                                        !(
                                          childItem.page_type === "materials" ||
                                          childItem.page_type === "material_item"
                                        ) && (
                                          <span
                                            className="ps-row__completed"
                                            style={{
                                              color: isChildActive
                                                ? "var(--page-selection-active-text)"
                                                : "var(--color-gray)",
                                            }}
                                          >
                                            {(
                                              childItem.page_type === "test"
                                                ? isAdmin
                                                  ? flattenedStatuses[
                                                      childItem.id
                                                    ]?.completed
                                                  : flattenedStatuses[
                                                      childItem.id
                                                    ]?.completed &&
                                                    flattenedStatuses[
                                                      childItem.id
                                                    ]?.content_quiz_completed
                                                : flattenedStatuses[
                                                    childItem.id
                                                  ]?.completed
                                            )
                                              ? "Completed"
                                              : "Incomplete"}
                                          </span>
                                        )}
                                    </div>
                                    {hasChildMcq && (
                                      <div className="mcq-index-right">
                                        <span className="mcq-index-count">
                                          {answeredCount}/{totalCount}
                                        </span>
                                        <div
                                          onClick={(e) => {
                                            if (isChildLocked) return;
                                            e.stopPropagation();
                                            setIsMcqCollapsed(!isMcqCollapsed);
                                          }}
                                          style={{
                                            cursor: isChildLocked
                                              ? "not-allowed"
                                              : "pointer",
                                            display: "flex",
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={
                                              isMcqCollapsed
                                                ? faChevronDown
                                                : faChevronUp
                                            }
                                            className="mcq-index-arrow"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {hasChildMcq && !isMcqCollapsed && (
                                    <div
                                      onClick={(e) => {
                                        if (isChildLocked) return;
                                        e.stopPropagation();
                                      }}
                                      style={{
                                        cursor: isChildLocked
                                          ? "not-allowed"
                                          : "default",
                                        width: "100%",
                                      }}
                                    >
                                      <MCQSummary
                                        {...mcqProps}
                                        contentTitle={childItem.name}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="ps-empty">{emptyText}</div>
            )}
          </div>
          <button
            type="button"
            className="ps-collapse-btn"
            onClick={() => setIsPanelCollapsed(true)}
          >
            <KeyboardDoubleArrowRight fontSize="small" />
            <span className="ps-collapse-text">Collapse</span>
          </button>
        </>
      )}
    </div>
  );
};

export default PageSelection;
